// Virtual Buyer API client - thin typed wrappers over the shared axios
// instance (`@/services/api`). Auth is passed PER REQUEST (not relied on from
// the global default header), matching the app convention (`getUser(token)`,
// `ProjectsService.getAuthHeader`). This avoids a cold-load race where the
// global `Authorization` header is set by a separate effect that can lag the
// first request → 401. VB-0 scaffolds the read surface; write methods land
// in later stories.

import api from "@/services/api";
import type { AnalyticsSource } from "@/types/analytics";
import type {
  AnalysisDetail,
  AnalysisShadow,
  Buyer,
  BuyerCompetitor,
  Big5Category,
  CompetitorInput,
  CreateBuyerPayload,
  Paginated,
  Question,
  ReportSourcesResponse,
  SourceDetail,
  SuggestDraft,
  SuggestResponse,
} from "@/types/vb";

/** Per-request auth header (JWT), matching the app-wide convention. */
function authConfig(token: string | null | undefined) {
  return token ? { headers: { Authorization: `JWT ${token}` } } : undefined;
}

/** GET /api/buyers/ - buyers in the caller's org, optionally scoped to one
 *  company via `?company=<id>`. List endpoints use DRF pagination, so unwrap
 *  `.results`. */
async function listBuyers(token: string | null, companyId?: number): Promise<Buyer[]> {
  const { data } = await api.get<Paginated<Buyer>>("/buyers/", {
    ...authConfig(token),
    ...(companyId != null ? { params: { company: companyId } } : {}),
  });
  return data.results;
}

/** GET /api/buyers/<id>/ */
async function getBuyer(buyerId: number, token: string | null): Promise<Buyer> {
  const { data } = await api.get<Buyer>(`/buyers/${buyerId}/`, authConfig(token));
  return data;
}

/** GET /api/buyers/<id>/analyses/ - shadow rows for a buyer (paginated). */
async function listAnalyses(buyerId: number, token: string | null): Promise<AnalysisShadow[]> {
  const { data } = await api.get<Paginated<AnalysisShadow>>(
    `/buyers/${buyerId}/analyses/`,
    authConfig(token),
  );
  return data.results;
}

/** GET /api/buyers/<id>/analyses/<id>/ - shadow + live progress or final result. */
async function getAnalysis(
  buyerId: number,
  analysisId: number,
  token: string | null,
): Promise<AnalysisDetail> {
  const { data } = await api.get<AnalysisDetail>(
    `/buyers/${buyerId}/analyses/${analysisId}/`,
    authConfig(token),
  );
  return data;
}

/** GET /api/buyers/<id>/questions/ - the buyer's questions (bare array). */
async function listQuestions(buyerId: number, token: string | null): Promise<Question[]> {
  const { data } = await api.get<Question[]>(
    `/buyers/${buyerId}/questions/`,
    authConfig(token),
  );
  return data;
}

// ---------------------------------------------------------------------------
// Writes (VB-B1 + VB-B2). The company is identified by id everywhere - the
// wizard enters scoped to a pre-selected `?company=<id>` and never handles a
// URL. Auth is per-request, same as the reads above.
// ---------------------------------------------------------------------------

/** POST /api/buyers/suggest/ - LLM persona suggestion for a company (no persist).
 *  422 CONTEXT_MISSING if the company has no inferred_context yet. An optional
 *  `draft` (the user's in-progress buyer) biases the suggestion so it fills the
 *  gaps around their input instead of replacing it. */
async function suggestBuyer(
  companyId: number,
  token: string | null,
  draft?: SuggestDraft,
): Promise<SuggestResponse> {
  const body: { company: number; draft?: SuggestDraft } = { company: companyId };
  if (draft && Object.values(draft).some((v) => (v ?? "").trim())) body.draft = draft;
  const { data } = await api.post<SuggestResponse>(
    "/buyers/suggest/",
    body,
    authConfig(token),
  );
  return data;
}

/** POST /api/buyers/company-context/ - ensure the buyer-side company context
 *  bundle exists for `companyId` (scrapes the site on first call). Idempotent;
 *  keyed by company id so it lands on the exact row the buyer references. The
 *  wizard calls this on entry and shows a loading state until it resolves. */
async function ensureCompanyContext(
  companyId: number,
  token: string | null,
): Promise<{ id: number; context: string[]; inferred_region: string }> {
  const { data } = await api.post(
    "/buyers/company-context/",
    { company: companyId },
    authConfig(token),
  );
  return data;
}

/** POST /api/buyers/ - create a buyer (+ optional nested competitors). */
async function createBuyer(
  payload: CreateBuyerPayload,
  token: string | null,
  source?: AnalyticsSource,
): Promise<Buyer> {
  const { data } = await api.post<Buyer>(
    "/buyers/",
    { ...payload, ...(source ? { source } : {}) },
    authConfig(token),
  );
  return data;
}

/** PATCH /api/buyers/<id>/ - partial update of scalar persona fields. */
async function patchBuyer(
  buyerId: number,
  partial: Partial<Pick<Buyer, "name" | "description" | "market_region" | "market_city" | "language">>,
  token: string | null,
): Promise<Buyer> {
  const { data } = await api.patch<Buyer>(`/buyers/${buyerId}/`, partial, authConfig(token));
  return data;
}

/** POST /api/buyers/<id>/questions/generate/ - replace-all, returns 10 (2×5). */
async function generateQuestions(
  buyerId: number,
  token: string | null,
  source?: AnalyticsSource,
): Promise<Question[]> {
  const { data } = await api.post<Question[]>(
    `/buyers/${buyerId}/questions/generate/`,
    source ? { source } : undefined,
    authConfig(token),
  );
  return data;
}

/** POST /api/buyers/<id>/questions/ - manual add (source MANUAL). */
async function addQuestion(
  buyerId: number,
  body: { category: Big5Category; text: string },
  token: string | null,
): Promise<Question> {
  const { data } = await api.post<Question>(
    `/buyers/${buyerId}/questions/`,
    body,
    authConfig(token),
  );
  return data;
}

/** PATCH /api/questions/<uuid>/ - edit text (source flips GENERATED→EDITED). */
async function updateQuestion(
  questionId: string,
  text: string,
  token: string | null,
): Promise<Question> {
  const { data } = await api.patch<Question>(
    `/questions/${questionId}/`,
    { text },
    authConfig(token),
  );
  return data;
}

/** DELETE /api/questions/<uuid>/ */
async function deleteQuestion(questionId: string, token: string | null): Promise<void> {
  await api.delete(`/questions/${questionId}/`, authConfig(token));
}

/** POST /api/buyers/<id>/competitors/ - add one competitor (≤5 cap). */
async function addCompetitor(
  buyerId: number,
  body: CompetitorInput,
  token: string | null,
): Promise<BuyerCompetitor> {
  const { data } = await api.post<BuyerCompetitor>(
    `/buyers/${buyerId}/competitors/`,
    body,
    authConfig(token),
  );
  return data;
}

/** DELETE /api/competitors/<id>/ */
async function deleteCompetitor(competitorId: number, token: string | null): Promise<void> {
  await api.delete(`/competitors/${competitorId}/`, authConfig(token));
}

/** POST /api/buyers/<id>/analyses/ - trigger a run, returns the shadow row. */
async function triggerAnalysis(
  buyerId: number,
  token: string | null,
  source?: AnalyticsSource,
): Promise<AnalysisShadow> {
  const { data } = await api.post<AnalysisShadow>(
    `/buyers/${buyerId}/analyses/`,
    source ? { source } : undefined,
    authConfig(token),
  );
  return data;
}

/** DELETE /api/buyers/<id>/ - best-effort orphan cleanup on wizard cancel. */
async function deleteBuyer(buyerId: number, token: string | null): Promise<void> {
  await api.delete(`/buyers/${buyerId}/`, authConfig(token));
}

/** GET /api/reports/<id>/sources/ - RGS per-report citation rollup + source rows
 *  (RGS-3). Paid-only and workspace-scoped on the backend; the X-Workspace-ID
 *  header is added by the axios interceptor. Feeds the reputation readout card. */
async function getReportSources(
  reportId: number,
  token: string | null,
): Promise<ReportSourcesResponse> {
  const { data } = await api.get<ReportSourcesResponse>(
    `/reports/${reportId}/sources/`,
    authConfig(token),
  );
  return data;
}

/** GET /api/reports/<id>/sources/<source_id>/ - one cited page's drill-down
 *  (RGS-3 detail). Lazy-fetched when the Sources page drawer opens. Same
 *  paid/workspace gating as the list endpoint. */
async function getReportSource(
  reportId: number,
  sourceId: string,
  token: string | null,
): Promise<SourceDetail> {
  const { data } = await api.get<SourceDetail>(
    `/reports/${reportId}/sources/${sourceId}/`,
    authConfig(token),
  );
  return data;
}

export const vbClient = {
  listBuyers,
  getBuyer,
  listAnalyses,
  getAnalysis,
  listQuestions,
  suggestBuyer,
  ensureCompanyContext,
  createBuyer,
  patchBuyer,
  generateQuestions,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  addCompetitor,
  deleteCompetitor,
  triggerAnalysis,
  deleteBuyer,
  getReportSources,
  getReportSource,
};

export default vbClient;
